import { useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

interface SovereignDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  side?: 'right' | 'bottom';
  width?: number;
  children: React.ReactNode;
}

export default function SovereignDrawer({
  isOpen,
  onClose,
  title,
  side = 'right',
  width = 450,
  children,
}: SovereignDrawerProps) {
  const drawerRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Determine if mobile (auto bottom-sheet for <768px)
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const effectiveSide = isMobile ? 'bottom' : side;

  // Focus trap
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isOpen) return;

      if (e.key === 'Escape') {
        onClose();
        return;
      }

      if (e.key === 'Tab' && drawerRef.current) {
        const focusableElements = drawerRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const firstEl = focusableElements[0] as HTMLElement;
        const lastEl = focusableElements[focusableElements.length - 1] as HTMLElement;

        if (e.shiftKey) {
          if (document.activeElement === firstEl) {
            e.preventDefault();
            lastEl?.focus();
          }
        } else {
          if (document.activeElement === lastEl) {
            e.preventDefault();
            firstEl?.focus();
          }
        }
      }
    },
    [isOpen, onClose]
  );

  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement as HTMLElement;
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';

      // Focus drawer after animation
      setTimeout(() => {
        const firstFocusable = drawerRef.current?.querySelector(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        ) as HTMLElement;
        firstFocusable?.focus();
      }, 100);
    } else {
      document.body.style.overflow = '';
      previousFocusRef.current?.focus();
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  const drawerVariants =
    effectiveSide === 'right'
      ? {
          hidden: { x: '100%', opacity: 0 },
          visible: { x: 0, opacity: 1, transition: { type: 'spring', damping: 25, stiffness: 300 } },
          exit: { x: '100%', opacity: 0, transition: { duration: 0.2 } },
        }
      : {
          hidden: { y: '100%', opacity: 0 },
          visible: { y: 0, opacity: 1, transition: { type: 'spring', damping: 25, stiffness: 300 } },
          exit: { y: '100%', opacity: 0, transition: { duration: 0.2 } },
        };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Scrim */}
          <motion.div
            className="fixed inset-0 z-[998] bg-black/60 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            aria-hidden="true"
          />

          {/* Drawer */}
          <motion.div
            ref={drawerRef}
            role="dialog"
            aria-modal="true"
            aria-label={title}
            className={`fixed z-[999] bg-[#121212] border-white/10 flex flex-col ${
              effectiveSide === 'right'
                ? 'top-0 right-0 h-full border-l'
                : 'bottom-0 left-0 right-0 max-h-[85vh] border-t rounded-t-2xl'
            }`}
            style={effectiveSide === 'right' ? { width: `${width}px`, maxWidth: '90vw' } : {}}
            variants={drawerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            {/* Handle bar for bottom sheet */}
            {effectiveSide === 'bottom' && (
              <div className="flex justify-center pt-3 pb-1">
                <div className="w-10 h-1 rounded-full bg-white/20" />
              </div>
            )}

            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
              <h2 className="text-lg font-bold text-white">{title}</h2>
              <button
                onClick={onClose}
                className="p-2 rounded-lg hover:bg-white/10 transition-colors text-white/60 hover:text-white"
                aria-label="Close drawer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-5 py-4">
              {children}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
